import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  anchorSteps,
  computeAnchoredTimes,
} from '../utils/cookingSchedule';

const STEP_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
};

const RESOURCE_CAPACITIES = {
  cook: 1,
  oven: 1,
  mixer: 1,
  stove: 4,
};

function createInitialStepState(step) {
  return {
    status: STEP_STATUS.PENDING,
    startedAt: null,
    pausedAt: null,
    accumulatedPauseMs: 0,
    completedAt: null,
    passive: step.kind === 'passive',
  };
}

function dependenciesMet(step, stepStates) {
  return (step.dependencies || []).every(
    (depId) => stepStates[depId]?.status === STEP_STATUS.COMPLETED
  );
}

function requiredResources(step) {
  const resources = [...(step.resources || [])];
  if (step.kind === 'active') {
    resources.push('cook');
  }
  return resources;
}

function isRunningStatus(status) {
  return status === STEP_STATUS.ACTIVE || status === STEP_STATUS.PAUSED;
}

function countRunningForResource(steps, stepStates, resource) {
  return steps.filter((step) => {
    const status = stepStates[step.id]?.status;
    if (!isRunningStatus(status)) {
      return false;
    }
    return requiredResources(step).includes(resource);
  }).length;
}

export function hasResourceCapacity(step, steps, stepStates) {
  for (const resource of requiredResources(step)) {
    const capacity = RESOURCE_CAPACITIES[resource] || 1;
    if (countRunningForResource(steps, stepStates, resource) >= capacity) {
      return false;
    }
  }
  return true;
}

export function getResourceBlockReason(step, steps, stepStates) {
  for (const resource of requiredResources(step)) {
    const capacity = RESOURCE_CAPACITIES[resource] || 1;
    if (countRunningForResource(steps, stepStates, resource) >= capacity) {
      if (resource === 'cook') {
        return 'Waiting for your attention — another active step is running.';
      }
      return `Waiting for ${resource} — equipment is in use.`;
    }
  }
  return null;
}

function getEffectiveElapsedMin(sessionStartedAt, now, steps, stepStates) {
  const realElapsedMin = (now - sessionStartedAt) / 60000;
  let completedScheduleMin = 0;

  steps.forEach((step) => {
    if (stepStates[step.id]?.status === STEP_STATUS.COMPLETED) {
      completedScheduleMin = Math.max(completedScheduleMin, step.end);
    }
  });

  return Math.max(realElapsedMin, completedScheduleMin);
}

function isDueToStart(step, stepStates, sessionStartedAt, now, steps) {
  const status = stepStates[step.id]?.status;
  if (status !== STEP_STATUS.PENDING) {
    return false;
  }
  if (!dependenciesMet(step, stepStates)) {
    return false;
  }

  const depIds = step.dependencies || [];
  if (depIds.length > 0) {
    const depTimes = depIds.map((depId) => stepStates[depId]?.completedAt);
    if (!depTimes.every(Boolean)) {
      return false;
    }
    return now >= Math.max(...depTimes);
  }

  const effectiveElapsedMin = getEffectiveElapsedMin(
    sessionStartedAt,
    now,
    steps,
    stepStates
  );
  return step.start <= effectiveElapsedMin;
}

function syncSessionToSchedule(steps, states, sessionStartedAt, now) {
  let next = { ...states };
  let changed = true;

  while (changed) {
    changed = false;

    const candidates = steps
      .filter((step) => isDueToStart(step, next, sessionStartedAt, now, steps))
      .sort((a, b) => a.start - b.start || a.end - b.end || a.id.localeCompare(b.id));

    candidates.forEach((step) => {
      if (!hasResourceCapacity(step, steps, next)) {
        return;
      }

      next[step.id] = {
        ...next[step.id],
        status: STEP_STATUS.ACTIVE,
        startedAt: next[step.id].startedAt || now,
        pausedAt: null,
      };
      changed = true;
    });
  }

  return next;
}

function getElapsedMs(state, now) {
  if (!state.startedAt) {
    return 0;
  }
  if (state.status === STEP_STATUS.PAUSED && state.pausedAt) {
    return state.pausedAt - state.startedAt - state.accumulatedPauseMs;
  }
  return now - state.startedAt - state.accumulatedPauseMs;
}

export function getStepRemainingMs(step, state, now = Date.now()) {
  if (!state || state.status === STEP_STATUS.COMPLETED) {
    return 0;
  }
  const durationMs = step.duration * 60 * 1000;
  const elapsed = getElapsedMs(state, now);
  return Math.max(0, durationMs - elapsed);
}

export function getStepProgress(step, state, now = Date.now()) {
  const durationMs = step.duration * 60 * 1000;
  if (!durationMs) {
    return 0;
  }
  const elapsed = getElapsedMs(state, now);
  return Math.min(100, Math.round((elapsed / durationMs) * 100));
}

export function useCookingSession(steps, totalMinutes, anchorMode, anchorDate) {
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [stepStates, setStepStates] = useState(() =>
    Object.fromEntries(steps.map((step) => [step.id, createInitialStepState(step)]))
  );
  const [now, setNow] = useState(Date.now());

  const stepIdsKey = useMemo(() => steps.map((step) => step.id).join('|'), [steps]);

  useEffect(() => {
    setStepStates(
      Object.fromEntries(steps.map((step) => [step.id, createInitialStepState(step)]))
    );
    setSessionActive(false);
    setSessionStartedAt(null);
  }, [stepIdsKey, steps]);

  useEffect(() => {
    if (!sessionActive) {
      return undefined;
    }
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [sessionActive]);

  useEffect(() => {
    if (!sessionActive || !sessionStartedAt) {
      return;
    }
    setStepStates((prev) => syncSessionToSchedule(steps, prev, sessionStartedAt, now));
  }, [sessionActive, sessionStartedAt, now, steps]);

  const { startAt, serveAt } = useMemo(
    () => computeAnchoredTimes(totalMinutes, anchorMode, anchorDate),
    [totalMinutes, anchorMode, anchorDate]
  );

  const planStartAt = sessionStartedAt || startAt;

  const anchoredSteps = useMemo(
    () => anchorSteps(steps, planStartAt),
    [steps, planStartAt]
  );

  const completeStep = useCallback((stepId) => {
    setStepStates((prev) => {
      const completedAt = Date.now();
      const next = {
        ...prev,
        [stepId]: {
          ...prev[stepId],
          status: STEP_STATUS.COMPLETED,
          completedAt,
          pausedAt: null,
        },
      };

      if (!sessionStartedAt) {
        return next;
      }

      return syncSessionToSchedule(steps, next, sessionStartedAt, completedAt);
    });
  }, [sessionStartedAt, steps]);

  const pauseStep = useCallback((stepId) => {
    setStepStates((prev) => {
      const current = prev[stepId];
      if (!current || current.status !== STEP_STATUS.ACTIVE) {
        return prev;
      }
      return {
        ...prev,
        [stepId]: {
          ...current,
          status: STEP_STATUS.PAUSED,
          pausedAt: Date.now(),
        },
      };
    });
  }, []);

  const resumeStep = useCallback((stepId) => {
    setStepStates((prev) => {
      const current = prev[stepId];
      if (!current || current.status !== STEP_STATUS.PAUSED || !current.pausedAt) {
        return prev;
      }
      return {
        ...prev,
        [stepId]: {
          ...current,
          status: STEP_STATUS.ACTIVE,
          accumulatedPauseMs:
            current.accumulatedPauseMs + (Date.now() - current.pausedAt),
          pausedAt: null,
        },
      };
    });
  }, []);

  const startSession = useCallback(() => {
    const startedAt = Date.now();
    setSessionActive(true);
    setSessionStartedAt(startedAt);
    setNow(startedAt);
    setStepStates((prev) =>
      syncSessionToSchedule(steps, prev, startedAt, startedAt)
    );
  }, [steps]);

  const activeSteps = useMemo(
    () =>
      [...anchoredSteps]
        .filter((step) => isRunningStatus(stepStates[step.id]?.status))
        .sort((a, b) => a.start - b.start || a.end - b.end),
    [anchoredSteps, stepStates]
  );

  const upcomingSteps = useMemo(() => {
    if (!sessionStartedAt) {
      return [...anchoredSteps].sort((a, b) => a.start - b.start).slice(0, 8);
    }

    return anchoredSteps
      .filter((step) => stepStates[step.id]?.status === STEP_STATUS.PENDING)
      .sort((a, b) => a.start - b.start || a.end - b.end)
      .slice(0, 8);
  }, [anchoredSteps, stepStates, sessionStartedAt]);

  const waitingSteps = useMemo(() => {
    if (!sessionStartedAt) {
      return [];
    }

    return anchoredSteps.filter((step) => {
      if (!isDueToStart(step, stepStates, sessionStartedAt, now, anchoredSteps)) {
        return false;
      }
      return !hasResourceCapacity(step, anchoredSteps, stepStates);
    });
  }, [anchoredSteps, stepStates, sessionStartedAt, now]);

  const completedCount = useMemo(
    () =>
      Object.values(stepStates).filter((state) => state.status === STEP_STATUS.COMPLETED)
        .length,
    [stepStates]
  );

  const equipmentStatus = useMemo(() => {
    const resources = ['oven', 'stove', 'mixer'];
    return resources.map((resource) => {
      const active = anchoredSteps.filter((step) => {
        const status = stepStates[step.id]?.status;
        return isRunningStatus(status) && step.resources?.includes(resource);
      });

      const capacity = RESOURCE_CAPACITIES[resource] || 1;

      return {
        resource,
        busy: active.length > 0,
        capacity,
        inUse: active.length,
        tasks: active,
      };
    });
  }, [anchoredSteps, stepStates]);

  return {
    sessionActive,
    sessionStartedAt,
    stepStates,
    anchoredSteps,
    startAt,
    serveAt,
    now,
    activeSteps,
    upcomingSteps,
    waitingSteps,
    completedCount,
    equipmentStatus,
    startSession,
    pauseStep,
    resumeStep,
    completeStep,
    getResourceBlockReason: (step) =>
      getResourceBlockReason(step, anchoredSteps, stepStates),
    STEP_STATUS,
  };
}
